import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FieldArray, Formik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from '../../hooks/useTranslation';
import { formatCep, formatCpfCnpj, formatPhone, validateCNPJ, validateCPF } from '../../utils';
import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PhoneInput from '../../components/PhoneInput';

const ConfirmCloseModal = ({ open, onConfirm, onCancel }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ pb: 1 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <WarningAmberIcon sx={{ color: '#f59e0b' }} />
        <Typography fontWeight="700" fontSize={16}>
          Alterações não salvas
        </Typography>
      </Box>
    </DialogTitle>

    <DialogContent>
      <Typography color="text.secondary" fontSize={14}>
        Possuem alterações não salvas.
        <br />
        Deseja realmente fechar?
      </Typography>
    </DialogContent>

    <Box display="flex" justifyContent="flex-end" gap={1} px={2} pb={2} pt={1}>
      <Button
        variant="outlined"
        onClick={onCancel}
        sx={{ textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}
      >
        Não
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        sx={{
          textTransform: 'none',
          backgroundColor: '#ef4444',
          '&:hover': { backgroundColor: '#dc2626' },
        }}
      >
        Cancelar
      </Button>
    </Box>
  </Dialog>
);

const taxRegimes = [
  { value: 'simples', label: 'Simples Nacional' },
  { value: 'lucro_presumido', label: 'Lucro Presumido' },
  { value: 'lucro_real', label: 'Lucro Real' },
];

const states = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const isCnpj = (value) => {
  if (!value) return false;
  const onlyDigits = value.replace(/\D/g, '');
  return onlyDigits.length === 14;
};

const AddressFields = ({ values, errors, touched, handleChange, handleBlur, setFieldValue, loadingCep, fetchAddressByCep, t }) => (
  <>
    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mb={2}>
      <Box>
        <Typography variant="body2" mb={0.5}>
          {t('forms.zipCode')}<span style={{ color: 'red' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder="00000-000"
          variant="outlined"
          size="small"
          name="cep"
          required
          value={formatCep(values.cep)}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/\D/g, '');
            handleChange({ target: { name: 'cep', value: cleaned } });
          }}
          onBlur={(e) => {
            handleBlur(e);
            const cleaned = e.target.value.replace(/\D/g, '');
            if (cleaned.length === 8) fetchAddressByCep(cleaned, setFieldValue);
          }}
          inputProps={{ maxLength: 9 }}
          disabled={loadingCep}
          error={touched.cep && Boolean(errors.cep)}
          helperText={loadingCep ? t('messages.fetchingCep') : touched.cep && errors.cep}
        />
      </Box>

      <Box>
        <Typography variant="body2" mb={0.5}>
          {t('forms.state')}<span style={{ color: 'red' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          select
          variant="outlined"
          size="small"
          name="state"
          required
          value={values.state}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={loadingCep}
          error={touched.state && Boolean(errors.state)}
          helperText={touched.state && errors.state}
        >
          <MenuItem value="">{t('forms.select')}</MenuItem>
          {states.map((state) => (
            <MenuItem key={state} value={state}>{state}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Box>
        <Typography variant="body2" mb={0.5}>
          {t('forms.city')}<span style={{ color: 'red' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          name="city"
          required
          value={values.city}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t('forms.city')}
          disabled={loadingCep}
          error={touched.city && Boolean(errors.city)}
          helperText={touched.city && errors.city}
        />
      </Box>
    </Box>

    <Box display="grid" gridTemplateColumns="2fr 1fr 1fr" gap={2} mb={2}>
      <Box>
        <Typography variant="body2" mb={0.5}>
          {t('forms.place')}<span style={{ color: 'red' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder={t('forms.streetPlaceholder')}
          variant="outlined"
          size="small"
          name="address"
          required
          value={values.address}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={loadingCep}
          error={touched.address && Boolean(errors.address)}
          helperText={touched.address && errors.address}
        />
      </Box>
      <Box>
        <Typography variant="body2" mb={0.5}>
          {t('forms.number')}<span style={{ color: 'red' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder={t('forms.numberPlaceholder')}
          variant="outlined"
          size="small"
          name="number"
          required
          value={values.number}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.number && Boolean(errors.number)}
          helperText={touched.number && errors.number}
        />
      </Box>
      <Box>
        <Typography variant="body2" mb={0.5}>
          {t('forms.complement')}
        </Typography>
        <TextField
          fullWidth
          placeholder={t('forms.complementPlaceholder')}
          variant="outlined"
          size="small"
          name="complement"
          value={values.complement}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Box>
    </Box>

    <Box>
      <Typography variant="body2" mb={0.5}>
        {t('forms.neighborhood')}<span style={{ color: 'red' }}>*</span>
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        name="neighborhood"
        required
        value={values.neighborhood || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={t('forms.neighborhood')}
        disabled={loadingCep}
        error={touched.neighborhood && Boolean(errors.neighborhood)}
        helperText={touched.neighborhood && errors.neighborhood}
      />
    </Box>
  </>
);

const ModalRegisterClient = ({ open, onClose, onSubmit, loading, initialData, isEditing }) => {
  const { t } = useTranslation();
  const [expandedCredit, setExpandedCredit] = useState(true);
  const [loadingCep, setLoadingCep] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const initialValues = {
    fullName: initialData?.full_name || '',
    email: initialData?.email || '',
    phone: initialData?.cellphone || '',
    taxvar: initialData?.taxvar || '',

    // opcionais
    businessName: initialData?.custom_fields?.business_name || '',
    municipalRegistration: initialData?.custom_fields?.municipal_registration || '',
    stateRegistration: initialData?.custom_fields?.state_registration || '',
    taxRegime: initialData?.custom_fields?.tax_regime || '',
    financialEmail: initialData?.custom_fields?.financial_email || '',
    financialPhone: initialData?.custom_fields?.financial_phone || '',
    address: initialData?.address?.place || '',
    number: initialData?.address?.number || '',
    complement: initialData?.address?.complement || '',
    neighborhood: initialData?.address?.neighborhood || '',
    cep: initialData?.address?.zipcode || '',
    state: typeof initialData?.address?.region === 'string'
      ? initialData.address.region
      : initialData?.address?.region?.name || '',
    city: initialData?.address?.city || '',

    partners: initialData?.relationships?.length
      ? initialData.relationships.map((rel) => ({
          id: rel.people?.id || '',
          fullName: rel.people?.full_name || '',
          email: rel.people?.email || '',
          phone: rel.people?.phone || '',
          taxvar: rel.people?.taxvar || '',
          document: rel.people?.document || '',
        }))
      : [{ fullName: '', email: '', phone: '', taxvar: '', document: '' }],
  };

  const hasPartnerData = (obj) => {
    return Object.values(obj).some((value) => value?.toString().trim());
  };

  const partnerSchema = yup.object().shape({
    fullName: yup
      .string()
      .test('required-if-filled', t('messages.fullNameRequired'), function (value) {
        if (hasPartnerData(this.parent) && !value) return false;
        return true;
      }),

    email: yup
      .string()
      .email(t('messages.invalidEmail'))
      .test('required-if-filled', t('messages.emailRequired'), function (value) {
        if (hasPartnerData(this.parent) && !value) return false;
        return true;
      }),

    phone: yup.string().nullable(),
    taxvar: yup.string().nullable(),
    document: yup.string().nullable(),
  });

  const schema = yup.object().shape({
    fullName: yup.string().required(t('messages.fullNameRequired')),
    email: yup.string().email(t('messages.invalidEmail')).required(t('messages.emailRequired')),
    phone: yup.string().required(t('messages.phoneRequired')),
    taxvar: yup
      .string()
      .required(t('forms.cpfCnpj'))
      .test('cpf-cnpj-valid', t('forms.invalidCpfCnpj'), (value) => isValidCpfCnpj(value)),
    cep: yup.string().required(t('messages.cepRequired')),
    state: yup.string().required(t('messages.stateRequired')),
    city: yup.string().required(t('messages.cityRequired')),
    address: yup.string().required(t('messages.addressRequired')),
    number: yup.string().required(t('messages.numberRequired')),
    neighborhood: yup.string().required(t('messages.neighborhoodRequired')),
    partners: yup.array().of(partnerSchema),
  });

  const isValidCpfCnpj = (value) => {
    if (!value) return false;

    const onlyDigits = value.replace(/\D/g, '');

    if (onlyDigits.length === 11) {
      return validateCPF(onlyDigits);
    }

    if (onlyDigits.length === 14) {
      return validateCNPJ(onlyDigits);
    }

    return false;
  };

  const fetchAddressByCep = async (cep, setFieldValue) => {
    const cleanedCep = cep.replace(/\D/g, '');

    if (cleanedCep.length !== 8) {
      return;
    }

    setLoadingCep(true);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanedCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.error(t('messages.zipCodeNotFound'));
        setLoadingCep(false);
        return;
      }

      setFieldValue('address', data.logradouro || '');
      setFieldValue('state', data.uf || '');
      setFieldValue('city', data.localidade || '');
      setFieldValue('neighborhood', data.bairro || '');
    } catch (error) {
      console.error(t('messages.errorFetchingData'), error);
      toast.error(t('messages.errorFetchingData'));
    } finally {
      setLoadingCep(false);
    }
  };

  const handleClose = () => {
    setExpandedCredit(true);
    setLoadingCep(false);
    setShowCloseConfirm(false);
    onClose();
  };

  const handleCloseAttempt = (dirty) => {
    if (dirty) {
      setShowCloseConfirm(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmClose = () => {
    handleClose();
  };

  const handleCancelConfirm = () => {
    setShowCloseConfirm(false);
  };

  const handleFormSubmit = (values) => {
    const cleanPayload = Object.fromEntries(
      Object.entries(values).filter(([_, value]) => value !== '' && value !== null)
    );

    const cleanPartners = isCnpj(values.taxvar)
      ? values.partners.filter((partner) => hasPartnerData(partner))
      : [];

    onSubmit({ ...cleanPayload, partners: cleanPartners });
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            borderRadius: 2,
          },
        }}
      >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleFormSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            dirty,
          }) => (
            <form onSubmit={handleSubmit}>
              <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5" fontWeight="700">
                    {isEditing ? t('titles.editClient') : t('titles.registerCustomer')}
                  </Typography>
                  <IconButton onClick={() => handleCloseAttempt(dirty)} size="small">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                <Box mb={3}>
                  <Typography variant="h6" fontWeight="700" mb={2} color="#4F4F4D">
                    {t('forms.clientData')}
                  </Typography>

                  <Box mb={2}>
                    <Typography variant="body1" mb={0.5}>
                      {t('forms.fullName')}
                      <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder={t('forms.fullName')}
                      variant="outlined"
                      size="small"
                      name="fullName"
                      value={values.fullName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.fullName && Boolean(errors.fullName)}
                      helperText={touched.fullName && errors.fullName}
                      inputProps={{ borderRadius: '8px' }}
                    />
                  </Box>

                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                    <Box>
                      <Typography variant="body1" mb={0.5}>
                        {t('forms.email')}
                        <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="email@email.com"
                        variant="outlined"
                        size="small"
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        inputProps={{ borderRadius: '8px' }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="body1" mb={0.5}>
                        {t('forms.phone')}
                        <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <PhoneInput
                        name="phone"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                      />
                    </Box>
                  </Box>

                  <Box mb={2} sx={{ maxWidth: '48%' }}>
                    <Typography variant="body1" mb={0.5}>
                      {t('forms.cpfCnpj')}
                      <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="00.000.000/0000-00"
                      variant="outlined"
                      size="small"
                      name="taxvar"
                      value={formatCpfCnpj(values.taxvar)}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/\D/g, '');
                        handleChange({ target: { name: 'taxvar', value: cleaned } });
                      }}
                      onBlur={handleBlur}
                      inputProps={{ maxLength: 18, borderRadius: '8px' }}
                      error={touched.taxvar && Boolean(errors.taxvar)}
                      helperText={touched.taxvar && errors.taxvar}
                    />
                  </Box>
                </Box>

                {/* Endereço do CPF — mesmo fluxo do collapse CNPJ */}
                {!isCnpj(values.taxvar) && (
                  <Box mb={3}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setExpandedCredit(!expandedCredit)}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#4F4F4D' }}>
                        {t('forms.cpfAddressSectionTitle')}
                        <Divider color="#4F4F4D" />
                      </Typography>
                      <IconButton size="small">
                        {expandedCredit ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>

                    <Collapse in={expandedCredit}>
                      <Box mt={2}>
                        <AddressFields
                          values={values}
                          errors={errors}
                          touched={touched}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          loadingCep={loadingCep}
                          fetchAddressByCep={fetchAddressByCep}
                          t={t}
                        />
                      </Box>
                    </Collapse>
                  </Box>
                )}

                {isCnpj(values.taxvar) && (
                  <Box mb={3}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setExpandedCredit(!expandedCredit)}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#4F4F4D' }}>
                        {t('forms.creditAnalysisRequired')}
                        <Divider color="#4F4F4D" />
                      </Typography>
                      <IconButton size="small">
                        {expandedCredit ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>

                    <Collapse in={expandedCredit}>
                      <Box mt={2}>
                        <Box mb={2}>
                          <Typography variant="body2" mb={0.5}>
                            {t('forms.companyName')}
                          </Typography>
                          <TextField
                            fullWidth
                            placeholder={t('forms.companyName')}
                            variant="outlined"
                            size="small"
                            name="businessName"
                            value={values.businessName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Box>

                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                          <Box>
                            <Typography variant="body2" mb={0.5}>
                              {t('forms.municipalRegistration')}
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="000000"
                              variant="outlined"
                              size="small"
                              name="municipalRegistration"
                              value={values.municipalRegistration}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Box>

                          <Box>
                            <Typography variant="body2" mb={0.5}>
                              {t('forms.stateRegistration')}
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="00000000000"
                              variant="outlined"
                              size="small"
                              name="stateRegistration"
                              value={values.stateRegistration}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Box>
                        </Box>

                        <Box mb={2}>
                          <Typography variant="body2" mb={0.5}>
                            {t('forms.regimeType')}
                          </Typography>
                          <TextField
                            fullWidth
                            select
                            variant="outlined"
                            size="small"
                            name="taxRegime"
                            value={values.taxRegime}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <MenuItem value="">{t('forms.select')}</MenuItem>
                            {taxRegimes.map((regime) => (
                              <MenuItem key={regime.value} value={regime.value}>
                                {regime.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>

                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                          <Box>
                            <Typography variant="body2" mb={0.5}>
                              {t('forms.financialEmail')}
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="email@email.com"
                              variant="outlined"
                              size="small"
                              type="email"
                              name="financialEmail"
                              value={values.financialEmail}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.financialEmail && Boolean(errors.financialEmail)}
                              helperText={touched.financialEmail && errors.financialEmail}
                            />
                          </Box>

                          <Box>
                            <Typography variant="body2" mb={0.5}>
                              {t('forms.phone')}
                            </Typography>
                            <PhoneInput
                              name="financialPhone"
                              value={values.financialPhone}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Box>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 3 }} />
                      {/* Endereço do CNPJ */}

                      <Box mb={3}>
                        <Typography variant="h6" fontWeight="600" mb={2}>
                          {t('forms.addressCnpj')}
                        </Typography>
                        <AddressFields
                          values={values}
                          errors={errors}
                          touched={touched}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          loadingCep={loadingCep}
                          fetchAddressByCep={fetchAddressByCep}
                          t={t}
                        />
                      </Box>

                      <Divider sx={{ my: 3 }} />

                      {/* Dados do Sócio */}
                      <Box mb={3}>
                        <Typography variant="h6" fontWeight="600" mb={2}>
                          {t('forms.partnerData')}
                        </Typography>

                        <FieldArray name="partners">
                          {({ push, remove }) => (
                            <>
                              {values.partners.map((partner, index) => (
                                <Box key={index} mb={3}>
                                  {index > 0 && (
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      mb={2}
                                    >
                                      <Typography variant="body1" fontWeight="500">
                                        {t('forms.partner')} {index + 1}
                                      </Typography>

                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() => remove(index)}
                                      >
                                        {t('buttons.remove')}
                                      </Button>
                                    </Box>
                                  )}

                                  {/* Nome */}
                                  <Box mb={2}>
                                    <Typography variant="body2" mb={0.5}>
                                      {t('forms.fullName')}
                                    </Typography>

                                    <TextField
                                      fullWidth
                                      name={`partners.${index}.fullName`}
                                      value={partner.fullName}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={
                                        touched.partners?.[index]?.fullName &&
                                        Boolean(errors.partners?.[index]?.fullName)
                                      }
                                      helperText={
                                        touched.partners?.[index]?.fullName &&
                                        errors.partners?.[index]?.fullName
                                      }
                                      size="small"
                                    />
                                  </Box>

                                  {/* Email + Telefone */}
                                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                                    <Box>
                                      <Typography variant="body2" mb={0.5}>
                                        {t('forms.email')}
                                      </Typography>

                                      <TextField
                                        fullWidth
                                        name={`partners.${index}.email`}
                                        value={partner.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        type="email"
                                        error={
                                          touched.partners?.[index]?.email &&
                                          Boolean(errors.partners?.[index]?.email)
                                        }
                                        helperText={
                                          touched.partners?.[index]?.email &&
                                          errors.partners?.[index]?.email
                                        }
                                        size="small"
                                      />
                                    </Box>

                                    <Box>
                                      <Typography variant="body2" mb={0.5}>
                                        {t('forms.phone')}
                                      </Typography>
                                      <PhoneInput
                                        name={`partners.${index}.phone`}
                                        value={partner.phone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                      />
                                    </Box>
                                  </Box>

                                  {/* CPF + Documento */}
                                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                                    <Box>
                                      <Typography variant="body2" mb={0.5}>
                                        {t('forms.cpf')}
                                      </Typography>

                                      <TextField
                                        fullWidth
                                        name={`partners.${index}.taxvar`}
                                        value={formatCpfCnpj(partner.taxvar)}
                                        size="small"
                                        onChange={(e) => {
                                          const cleaned = e.target.value.replace(/\D/g, '');
                                          setFieldValue(`partners.${index}.taxvar`, cleaned);
                                        }}
                                        inputProps={{ maxLength: 14 }}
                                      />
                                    </Box>

                                    <Box>
                                      <Typography variant="body2" mb={0.5}>
                                        {t('forms.document')}
                                      </Typography>

                                      <TextField
                                        fullWidth
                                        name={`partners.${index}.document`}
                                        value={partner.document}
                                        onChange={handleChange}
                                        size="small"
                                      />
                                    </Box>
                                  </Box>

                                  {index < values.partners.length - 1 && <Divider sx={{ my: 2 }} />}
                                </Box>
                              ))}

                              {/* Adicionar Sócio */}
                              <Button
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  push({
                                    fullName: '',
                                    email: '',
                                    phone: '',
                                    taxvar: '',
                                    document: '',
                                  })
                                }
                                sx={{
                                  color: '#4caf50',
                                  textTransform: 'none',
                                  fontWeight: 500,
                                }}
                              >
                                {t('forms.addPartner')}
                              </Button>
                            </>
                          )}
                        </FieldArray>
                      </Box>
                    </Collapse>
                  </Box>
                )}

                <Box display="flex" justifyContent="flex-end" mt={3}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: '#4caf50',
                      textTransform: 'none',
                      px: 4,
                      '&:hover': {
                        backgroundColor: '#45a049',
                      },
                    }}
                  >
                    {t('buttons.save')}
                  </Button>
                </Box>
              </DialogContent>
            </form>
          )}
        </Formik>
      </Dialog>

      <ConfirmCloseModal
        open={showCloseConfirm}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelConfirm}
      />
    </>
  );
};

export default ModalRegisterClient;
